import React from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  Box, 
  CircularProgress, 
  Stack,
  Tooltip
} from '@mui/material';

// Cores para cada etapa do Gantt
const etapaColors = {
  deslocamento: '#2196F3', // Azul
  intervalo: '#FFC107',    // Amarelo
  nao_apontado: '#F44336', // Vermelho
  servico: '#4CAF50',      // Verde
};

// Componente para renderizar o Gantt colorido
const GanttChart = ({ data }) => {
  if (!data || typeof data.tempo_medio_turno !== 'number' || data.tempo_medio_turno === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40 }}>
        <Typography variant="body2" color="text.secondary">--</Typography>
      </Box>
    );
  }

  const tempoTotal = Math.round(data.tempo_medio_turno);
  const deslocamento = Math.round(data.tempo_medio_deslocamento || 0);
  const intervalo = Math.round(data.tempo_medio_intervalo || 0);
  const naoApontado = Math.round(data.tempo_medio_nao_apontado || 0);
  const servico = Math.round(data.tempo_medio_servico || 0);

  // Calcular percentuais
  const getPercentage = (value) => {
    return tempoTotal > 0 ? (value / tempoTotal) * 100 : 0;
  };

  const deslocamentoPercent = getPercentage(deslocamento);
  const intervaloPercent = getPercentage(intervalo);
  const naoApontadoPercent = getPercentage(naoApontado);
  const servicoPercent = getPercentage(servico);

  // Soma dos tempos conhecidos (pode não somar exatamente o turno)
  const somaConhecida = deslocamento + intervalo + naoApontado + servico;
  const diferenca = Math.max(0, tempoTotal - somaConhecida);
  const diferencaPercent = getPercentage(diferenca);

  return (
    <Box sx={{ width: '100%', minWidth: 300 }}>
      {/* Barra Gantt */}
      <Box 
        sx={{ 
          display: 'flex', 
          width: '100%', 
          height: 32, 
          borderRadius: 1, 
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        {/* Deslocamento */}
        {deslocamentoPercent > 0 && (
          <Tooltip 
            title={`Deslocamento: ${deslocamento} min (${deslocamentoPercent.toFixed(1)}%)`}
            arrow
          >
            <Box
              sx={{
                width: `${deslocamentoPercent}%`,
                backgroundColor: etapaColors.deslocamento,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.8 },
                cursor: 'help',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid rgba(255,255,255,0.3)',
                position: 'relative',
                minWidth: deslocamentoPercent > 8 ? 'auto' : 0
              }}
            >
              {deslocamentoPercent > 8 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {deslocamento}m
                </Typography>
              )}
            </Box>
          </Tooltip>
        )}
        
        {/* Intervalo */}
        {intervaloPercent > 0 && (
          <Tooltip 
            title={`Intervalo: ${intervalo} min (${intervaloPercent.toFixed(1)}%)`}
            arrow
          >
            <Box
              sx={{
                width: `${intervaloPercent}%`,
                backgroundColor: etapaColors.intervalo,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.8 },
                cursor: 'help',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid rgba(255,255,255,0.3)',
                position: 'relative',
                minWidth: intervaloPercent > 8 ? 'auto' : 0
              }}
            >
              {intervaloPercent > 8 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {intervalo}m
                </Typography>
              )}
            </Box>
          </Tooltip>
        )}
        
        {/* Não Apontado */}
        {naoApontadoPercent > 0 && (
          <Tooltip 
            title={`Não Apontado: ${naoApontado} min (${naoApontadoPercent.toFixed(1)}%)`}
            arrow
          >
            <Box
              sx={{
                width: `${naoApontadoPercent}%`,
                backgroundColor: etapaColors.nao_apontado,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.8 },
                cursor: 'help',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid rgba(255,255,255,0.3)',
                position: 'relative',
                minWidth: naoApontadoPercent > 8 ? 'auto' : 0
              }}
            >
              {naoApontadoPercent > 8 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {naoApontado}m
                </Typography>
              )}
            </Box>
          </Tooltip>
        )}
        
        {/* Serviço */}
        {servicoPercent > 0 && (
          <Tooltip 
            title={`Serviço: ${servico} min (${servicoPercent.toFixed(1)}%)`}
            arrow
          >
            <Box
              sx={{
                width: `${servicoPercent}%`,
                backgroundColor: etapaColors.servico,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.8 },
                cursor: 'help',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid rgba(255,255,255,0.3)',
                position: 'relative',
                minWidth: servicoPercent > 8 ? 'auto' : 0
              }}
            >
              {servicoPercent > 8 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {servico}m
                </Typography>
              )}
            </Box>
          </Tooltip>
        )}
        
        {/* Diferença (tempo não categorizado) */}
        {diferencaPercent > 1 && (
          <Tooltip 
            title={`Outros: ${Math.round(diferenca)} min (${diferencaPercent.toFixed(1)}%)`}
            arrow
          >
            <Box
              sx={{
                width: `${diferencaPercent}%`,
                backgroundColor: '#9E9E9E',
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.8 },
                cursor: 'help',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                minWidth: diferencaPercent > 8 ? 'auto' : 0
              }}
            >
              {diferencaPercent > 8 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {Math.round(diferenca)}m
                </Typography>
              )}
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const TempoMovimentosContratosTable = ({ data = [], loading = false }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
        <CircularProgress size={48} thickness={4} />
      </Box>
    );
  }

  // Ordenar por tempo médio de turno (menor é melhor, mas vamos usar maior para mostrar quem trabalha mais)
  // Na verdade, vamos ordenar por algum critério relevante - vou ordenar pela soma eficiente de serviço
  const sorted = [...data].sort((a, b) => {
    // Calcular eficiência: maior % de serviço em relação ao turno é melhor
    const aServico = a.tempo_medio_servico || 0;
    const aTurno = a.tempo_medio_turno || 1;
    const aEficiencia = aServico / aTurno;

    const bServico = b.tempo_medio_servico || 0;
    const bTurno = b.tempo_medio_turno || 1;
    const bEficiencia = bServico / bTurno;

    return bEficiencia - aEficiencia; // Maior eficiência primeiro
  });

  return (
    <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3, mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Tempo e Movimentos
        </Typography>
      </Stack>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 80 }}>Ranking</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Contrato</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Tempos e Movimentos</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} align="center">
                <Typography color="text.secondary">Nenhum dado encontrado.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            sorted.map((row, idx) => {
              const rank = idx + 1;
              let rankColor = undefined;
              // Top 3 são verdes, médio amarelo, resto vermelho
              if (idx === 0 || idx === 1 || idx === 2) rankColor = 'success.main';
              else if (idx >= 3 && idx <= 6) rankColor = 'warning.main';
              else if (idx >= 7) rankColor = 'error.main';
              
              return (
                <TableRow key={row.des_contrato || idx} hover>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 18, color: rankColor }}>
                    {rank}
                  </TableCell>
                  <TableCell>{row.des_contrato}</TableCell>
                  <TableCell>
                    <GanttChart data={row} />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TempoMovimentosContratosTable;
